import React, { useEffect, useState } from "react";
import { Box, Dialog } from "@mui/material";

import styles from "pages/app-page/AppPage.module.css";

import appBannerSmall from "shared/assets/images/BannerAppPageSmall.png";
import policyImg from "shared/assets/images/policyImg.png";
import historyOfVisits from "shared/assets/images/historyOfVisits.png";
import eventCalendar from "shared/assets/images/eventCalendar.png";
import doctorImg from "shared/assets/images/doctorImg.png";
import qrPhone from "shared/assets/images/qrPhone.png";

import emptyPhone from "shared/assets/images/emptyPhone.png";

import CustomButton from "shared/custom-button/CustomButton";
import AppStoreImg from "shared/assets/AppStoreImg";
import GooglePlayImg from "shared/assets/GooglePlayImg";
import RuStoreImg from "shared/assets/RuStoreImg";

type DeviceType = "ios" | "android" | "desktop" | "unknown";

const STORE_LINKS = {
  ios: "https://apps.apple.com/ru/app/%D1%8E-%D0%B7%D0%B4%D0%BE%D1%80%D0%BE%D0%B2%D1%8C%D0%B5/id6744051740?l",
  android: "https://play.google.com/store/apps/details?id=com.skgaidemobile",
  ruStore: "https://www.rustore.ru/catalog/app/com.skgaidemobile",
};

const getDeviceType = (): DeviceType => {
  if (typeof navigator === "undefined") {
    return "unknown";
  }

  const userAgent = navigator.userAgent.toLowerCase();

  const isAndroid = /android/.test(userAgent);

  const isIOS =
    /iphone|ipad|ipod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (isAndroid) {
    return "android";
  }

  if (isIOS) {
    return "ios";
  }

  if (/windows|macintosh|linux/.test(userAgent)) {
    return "desktop";
  }

  return "unknown";
};

const scrollToElement = (id: string) => {
  const element = document.getElementById(id);

  if (!element) {
    return;
  }

  element.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
};

export const AppPage: React.FC = () => {
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [deviceType, setDeviceType] = useState<DeviceType>("unknown");

  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    setDeviceType(getDeviceType());
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleDownloadApp = () => {
    if (deviceType === "ios") {
      window.location.href = STORE_LINKS.ios;
      return;
    }

    if (deviceType === "android") {
      setIsStoreModalOpen(true);
      return;
    }

    scrollToElement("download");
  };
  const handleCloseStoreModal = () => {
    setIsStoreModalOpen(false);
  };

  const handleStoreClick = (store: "google" | "rustore") => {
    setIsStoreModalOpen(false);

    window.location.href =
      store === "google" ? STORE_LINKS.android : STORE_LINKS.ruStore;
  };
  return (
    <Box className={styles.content}>
      <Box className={styles.bannerBox}>
        <Box className={styles.bannerContent}>
          <Box className={styles.descriptionBox}>
            <h1>Ю-Здоровье</h1>

            <Box className={styles.description}>
              <p>
                Официальное мобильное приложение СК «Югория» для застрахованных
                по программам ДМС
              </p>
            </Box>

            <Box className={styles.btnBox}>
              <CustomButton
                handleClick={handleDownloadApp}
                title="Скачать"
                textColor="white"
                backgroundColor="#0054E6"
                border="none"
                borderRadius="14px"
                padding="8px 30px"
                disabled={false}
              />
            </Box>
          </Box>
          <Dialog
            open={isStoreModalOpen}
            onClose={handleCloseStoreModal}
            aria-labelledby="store-dialog-title"
            PaperProps={{
              className: styles.storeModal,
            }}
          >
            <Box className={styles.storeModalContent}>
              <button
                className={styles.storeModalClose}
                onClick={handleCloseStoreModal}
                aria-label="Закрыть"
              >
                ×
              </button>

              <h2 id="store-dialog-title">Скачать приложение</h2>

              <p>Выберите магазин для скачивания приложения «Ю-Здоровье»</p>
              <Box className={styles.storeModalLinks}>
                <a
                  href={STORE_LINKS.android}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GooglePlayImg />
                </a>
                <a
                  href={STORE_LINKS.ruStore}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <RuStoreImg />
                </a>
              </Box>
            </Box>
          </Dialog>

          <Box className={styles.imgBox}>
            <picture>
              <source media="(max-width: 1050px)" srcSet={appBannerSmall} />

              <img src={appBannerSmall} alt="Приложение Ю-здоровье" />
            </picture>
          </Box>
        </Box>
      </Box>

      <Box className={styles.anchorNavigation}>
        <button onClick={() => scrollToElement("policy")}>
          Страховой полис
        </button>

        <button onClick={() => scrollToElement("doctor")}>
          Запись к врачу
        </button>

        <button onClick={() => scrollToElement("eventCalendar")}>
          Календарь событий
        </button>

        <button onClick={() => scrollToElement("historyOfVisits")}>
          История посещений
        </button>
      </Box>

      <Box className={styles.sections}>
        <AppSection
          id="policy"
          title="Страховой полис"
          text="Храните все документы в цифровом формате. Ваш полис всегда в смартфоне. Проверяйте сроки и предъявляйте документ в один клик."
          image={policyImg}
        />

        <AppSection
          id="doctor"
          title="Запись к врачу"
          text="Больше никаких звонков и ожиданий на линии. Выбирайте клинику и удобное время в пару касаний. Планируйте здоровье без стресса."
          image={doctorImg}
          reverse
        />

        <AppSection
          id="eventCalendar"
          title="Календарь событий"
          text="Держите здоровье под контролем. Все предстоящие приемы и обследования собраны в одном месте. Не переживайте о забытых датах."
          image={eventCalendar}
        />

        <AppSection
          id="historyOfVisits"
          title="История посещений"
          text="Вся медицинская картина в приложении. Когда и к какому врачу вы были записаны. Удобный архив, для себя или для нового специалиста."
          image={historyOfVisits}
          reverse
        />
        <Box id="download" className={styles.downloadSection}>
          <Box className={styles.downloadContent}>
            <Box>
              <h2>Скачайте приложение</h2>

              <p>«Ю-Здоровье» доступно для устройств на iOS и Android</p>
            </Box>

            <Box className={styles.storeButtons}>
              <a
                href={STORE_LINKS.ios}
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppStoreImg />
              </a>
              <a
                href={STORE_LINKS.android}
                target="_blank"
                rel="noopener noreferrer"
              >
                <GooglePlayImg />
              </a>
              <a
                href={STORE_LINKS.ruStore}
                target="_blank"
                rel="noopener noreferrer"
              >
                <RuStoreImg />
              </a>
            </Box>
          </Box>
          <Box className={styles.downloadImage}>
            <img src={qrPhone} />
            <span className={styles.downloadImageText}>Наведите камеру на QR-код и скачайте приложение</span>
          </Box>
        </Box>
      </Box>

      {showScrollTop && (
        <button
          className={styles.scrollTopButton}
          onClick={() =>
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            })
          }
          aria-label="Наверх"
        >
          ↑
        </button>
      )}
    </Box>
  );
};

interface AppSectionProps {
  id: string;
  title: string;
  text: string;
  image: string;
  reverse?: boolean;
}

const AppSection: React.FC<AppSectionProps> = ({
  id,
  title,
  text,
  image,
  reverse = false,
}) => {
  return (
    <Box
      id={id}
      className={`${styles.appSection} ${reverse ? styles.reverse : ""}`}
    >
      <Box className={styles.sectionImage}>
        <img src={image} alt="" />
      </Box>

      <Box className={styles.sectionText}>
        <h2>{title}</h2>

        <p>{text}</p>
      </Box>
    </Box>
  );
};
